import {
	faChevronDown,
	faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { useState, type ReactElement } from 'react';
import { Icon } from '../../components/base/Icon';
import { Input } from '../../components/forms/Input';
import { SingleSelect } from '../../components/forms/SingleSelect';
import { TextArea } from '../../components/forms/TextArea';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import type { AvailableDataSource } from './DynamicFieldValue';
import { DynamicFieldValue } from './DynamicFieldValue';

/**
 * Configuration form for the `external_lookup` step.
 *
 * The step takes an existing cross-reference request produced by a `product_match`
 * step, looks up product facts on the web for the lines that did not match, folds
 * those facts onto the lines as additional evidence, and re-scores the request.
 *
 * The step is self-contained: what it is permitted to do is defined here and
 * nowhere else. The workflow it replaces is an open web search of the part
 * number, so an open search is the default: a blank `allowedDomains` means the
 * lookup may search the whole web, and `blockedDomains` excludes sites from
 * that search. Listing allowed domains instead restricts the lookup to those
 * sites, which already excludes everything else — so the two lists are
 * mutually exclusive.
 */

export interface ExternalLookupStepConfigProps {
	config: {
		requestId?: string;
		lineFilter?: 'no_match' | 'no_match_and_low_confidence' | 'all_pending';
		confidenceCeiling?: number;
		maxLines?: number;
		concurrency?: number;
		allowedDomains?: Array<string>;
		blockedDomains?: Array<string>;
		lookupConstraints?: string;
		maxSearches?: number;
		allowFetch?: boolean;
		maxFetches?: number;
		maxContentTokens?: number;
		timeoutSeconds?: number;
		wallClockSeconds?: number;
		maxTotalTokens?: number;
		requestTokenBudget?: number;
		model?: string;
		rematch?: boolean;
	};
	setConfig: (config: ExternalLookupStepConfigProps['config']) => void;
	getAvailableDataSources?: () => Array<AvailableDataSource>;
	getDisplayText?: (value: string) => string;
}

/**
 * Mirrors the server-side clamps on the step. Keeping the bounds here as well
 * means a stored config never carries a value the step would silently change.
 */
const DEFAULT_LINE_FILTER = 'no_match';
const MIN_CONFIDENCE_CEILING = 0;
const MAX_CONFIDENCE_CEILING = 100;
const DEFAULT_MAX_LINES = 50;
const MIN_MAX_LINES = 1;
const MAX_MAX_LINES = 200;
const DEFAULT_CONCURRENCY = 4;
const MIN_CONCURRENCY = 1;
const MAX_CONCURRENCY = 8;
const DEFAULT_MAX_SEARCHES = 3;
const MIN_MAX_SEARCHES = 1;
const MAX_MAX_SEARCHES = 5;
const DEFAULT_MAX_FETCHES = 2;
const MIN_MAX_FETCHES = 1;
const MAX_MAX_FETCHES = 5;
const MIN_MAX_CONTENT_TOKENS = 1000;
const MAX_MAX_CONTENT_TOKENS = 100000;
const DEFAULT_TIMEOUT_SECONDS = 180;
const MIN_TIMEOUT_SECONDS = 30;
const MAX_TIMEOUT_SECONDS = 600;
const DEFAULT_WALL_CLOCK_SECONDS = 300;
const MIN_WALL_CLOCK_SECONDS = 60;
const MAX_WALL_CLOCK_SECONDS = 1800;
const DEFAULT_MAX_TOTAL_TOKENS = 500000;
const MIN_MAX_TOTAL_TOKENS = 20000;
const MAX_MAX_TOTAL_TOKENS = 5000000;
const DEFAULT_REQUEST_TOKEN_BUDGET = 2000000;
const MIN_REQUEST_TOKEN_BUDGET = 100000;
const MAX_REQUEST_TOKEN_BUDGET = 20000000;
const MAX_LOOKUP_CONSTRAINTS_LENGTH = 2000;

/**
 * Which lines the step enriches. The default is the narrowest option: lines
 * that produced no match at all are the ones with the most to gain and the
 * least to lose from additional evidence.
 */
const LINE_FILTER_OPTIONS = [
	{ value: 'no_match', label: 'Lines with no match' },
	{
		value: 'no_match_and_low_confidence',
		label: 'Lines with no match or low confidence',
	},
	{ value: 'all_pending', label: 'All lines awaiting review' },
];

/** One domain per line, blank lines dropped. */
function parseDomains(text: string): Array<string> {
	return text
		.split('\n')
		.map((entry) => entry.trim())
		.filter((entry) => entry !== '');
}

interface DomainListDraft {
	/** The raw text currently in the textarea. */
	text: string;
	/** Records the typed text and returns the entries it parses to. */
	edit: (next: string) => Array<string>;
}

/**
 * A domain list is edited as free text and stored as an array. Rendering the
 * textarea straight from the array would drop a newline the moment it is typed
 * (a blank trailing line parses away), so the text being edited is held here.
 */
function useDomainListDraft(
	stored: Array<string> | undefined,
): DomainListDraft {
	const storedText = (stored ?? []).join('\n');
	const [text, setText] = useState(storedText);
	const [syncedText, setSyncedText] = useState(storedText);

	// Adopt a config replaced from outside the form — a step switch or a revert.
	// Ordinary typing always parses back to the text already on screen, so this
	// never fires mid-edit and never moves the caret.
	if (syncedText !== storedText) {
		setSyncedText(storedText);

		if (parseDomains(text).join('\n') !== storedText) {
			setText(storedText);
		}
	}

	const edit = (next: string): Array<string> => {
		setText(next);

		const entries = parseDomains(next);

		setSyncedText(entries.join('\n'));

		return entries;
	};

	return { text, edit };
}

export function ExternalLookupStepConfig({
	config,
	setConfig,
	getAvailableDataSources,
	getDisplayText,
}: ExternalLookupStepConfigProps): ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const [advancedExpanded, setAdvancedExpanded] = useState(false);

	const allowedDraft = useDomainListDraft(config.allowedDomains);
	const blockedDraft = useDomainListDraft(config.blockedDomains);

	const updateConfig = <
		K extends keyof ExternalLookupStepConfigProps['config'],
	>(
		key: K,
		value: ExternalLookupStepConfigProps['config'][K],
	): void => {
		setConfig({ ...config, [key]: value });
	};

	/** Clearing a numeric field falls back to the server default rather than zero. */
	const updateClampedNumber = (
		key:
			| 'confidenceCeiling'
			| 'maxLines'
			| 'concurrency'
			| 'maxSearches'
			| 'maxFetches'
			| 'maxContentTokens'
			| 'timeoutSeconds'
			| 'wallClockSeconds'
			| 'maxTotalTokens'
			| 'requestTokenBudget',
		raw: string,
		min: number,
		max: number,
	): void => {
		const parsed = parseInt(raw, 10);

		if (isNaN(parsed)) {
			updateConfig(key, undefined);
			return;
		}

		updateConfig(key, Math.min(max, Math.max(min, parsed)));
	};

	const lineFilter = config.lineFilter ?? DEFAULT_LINE_FILTER;
	const allowFetch = config.allowFetch === true;
	const lookupConstraintsValue = config.lookupConstraints ?? '';

	// The two domain lists are mutually exclusive. Whichever one is still empty is
	// disabled while the other holds entries, so the exclusivity reads off the
	// form instead of arriving as a validation error after the fact. Disabling is
	// deliberately conditioned on this list being empty: a stored config that
	// somehow carries both stays editable, so the operator can clear one and no
	// entered value is ever thrown away.
	const allowedEntries = config.allowedDomains ?? [];
	const blockedEntries = config.blockedDomains ?? [];
	const allowedDisabled =
		allowedEntries.length === 0 && blockedEntries.length > 0;
	const blockedDisabled =
		blockedEntries.length === 0 && allowedEntries.length > 0;

	return (
		<div className="space-y-6">
			{/* The request to enrich. Held as a variable reference to an earlier
			    Product Match step rather than an inlined id. */}
			<div>
				<h3
					className={`text-base font-semibold mb-4 ${colors.classes.textPrimary}`}
				>
					{t('External Lookup')}
				</h3>

				<DynamicFieldValue
					label={t('Request')}
					value={config.requestId ?? ''}
					onChange={(value) => {
						updateConfig('requestId', value);
					}}
					dataType="string"
					required={true}
					placeholder={t('Select the Request ID')}
					description={t(
						'The Request ID produced by an earlier Product Match step. Its lines are the ones this step enriches.',
					)}
					availableData={getAvailableDataSources?.() ?? []}
					displayTransform={getDisplayText}
				/>
			</div>

			{/* The lookup definition. This is the whole authority for what the step
			    may reach, so it leads the form. */}
			<div>
				<h4
					className={`text-sm font-semibold mb-1 ${colors.classes.textPrimary}`}
				>
					{t('Where to Look')}
				</h4>
				<p className={`text-xs mb-4 ${colors.classes.textSecondary}`}>
					{t(
						'This step carries its own lookup permissions. By default it searches the open web, within the budgets set below. The two domain lists narrow that: use one or the other, not both.',
					)}
				</p>

				{/* Open web by default — domains here restrict the lookup instead */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('Allowed Domains')}
					</label>
					<TextArea
						value={allowedDraft.text}
						onChange={(e) => {
							updateConfig('allowedDomains', allowedDraft.edit(e.target.value));
						}}
						rows={4}
						disabled={allowedDisabled}
						placeholder={t('hilti.com')}
					/>
					<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t(
							'Leave this blank and the lookup searches the open web — the same thing a person doing a manual web search does. Add domains only to restrict the lookup to specific sites.',
						)}
					</p>
					<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t(
							'One domain per line. Enter bare domains such as hilti.com — subdomains are included. Do not include https:// or a path.',
						)}
					</p>
					{allowedDisabled && (
						<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
							{t(
								'Unavailable while blocked domains are set. Clear the blocked list to restrict the lookup to specific sites instead.',
							)}
						</p>
					)}
				</div>

				{/* Exclusions from the open search — moot once the lookup is restricted */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('Blocked Domains')}
					</label>
					<TextArea
						value={blockedDraft.text}
						onChange={(e) => {
							// Absent, not an empty array, is what "nothing blocked" looks
							// like — clearing the field puts the config back to that.
							const entries = blockedDraft.edit(e.target.value);

							updateConfig(
								'blockedDomains',
								entries.length === 0 ? undefined : entries,
							);
						}}
						rows={4}
						disabled={blockedDisabled}
						placeholder={t('example-marketplace.com')}
					/>
					<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t(
							'Sites the lookup must never use. This only applies while Allowed Domains is blank, since a list of allowed sites already excludes everything else.',
						)}
					</p>
					<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t(
							'One domain per line. Enter bare domains such as example.com — subdomains are included. Do not include https:// or a path.',
						)}
					</p>
					{blockedDisabled && (
						<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
							{t(
								'Unavailable while the lookup is restricted to the allowed domains above. Clear that list to search the open web instead.',
							)}
						</p>
					)}
				</div>

				{/* Per-line search budget */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('Maximum Searches Per Line')}
					</label>
					<Input
						type="number"
						value={config.maxSearches ?? DEFAULT_MAX_SEARCHES}
						onChange={(e) => {
							updateClampedNumber(
								'maxSearches',
								e.target.value,
								MIN_MAX_SEARCHES,
								MAX_MAX_SEARCHES,
							);
						}}
						min={MIN_MAX_SEARCHES}
						max={MAX_MAX_SEARCHES}
					/>
					<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t(
							'How many search rounds the lookup may run for each line, one after another. Each round runs one search and passes on only what it found, and the rounds stop early once one of them has enough. Default 3, maximum 5.',
						)}
					</p>
				</div>

				{/* Page fetching, opt-in — snippets only until this is turned on */}
				<div className="mb-4">
					<label className="flex items-center gap-2 cursor-pointer">
						<input
							type="checkbox"
							checked={allowFetch}
							onChange={(e) => {
								updateConfig('allowFetch', e.target.checked);
							}}
						/>
						<span
							className={`text-sm font-medium ${colors.classes.textPrimary}`}
						>
							{t('Read full pages')}
						</span>
					</label>
					<p className={`ml-6 text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t(
							'Lets the lookup open a search result and read the page. Slower and more expensive than search snippets alone, but it finds specifications a snippet leaves out.',
						)}
					</p>

					{allowFetch && (
						<div className="ml-6 mt-3 grid grid-cols-2 gap-4">
							<div>
								<label
									className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
								>
									{t('Maximum Pages Per Line')}
								</label>
								<Input
									type="number"
									value={config.maxFetches ?? DEFAULT_MAX_FETCHES}
									onChange={(e) => {
										updateClampedNumber(
											'maxFetches',
											e.target.value,
											MIN_MAX_FETCHES,
											MAX_MAX_FETCHES,
										);
									}}
									min={MIN_MAX_FETCHES}
									max={MAX_MAX_FETCHES}
								/>
								<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
									{t(
										'How many pages the lookup may open for each line. Default 2, maximum 5.',
									)}
								</p>
							</div>

							<div>
								<label
									className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
								>
									{t('Page Content Limit')}
								</label>
								<Input
									type="number"
									value={config.maxContentTokens ?? ''}
									onChange={(e) => {
										updateClampedNumber(
											'maxContentTokens',
											e.target.value,
											MIN_MAX_CONTENT_TOKENS,
											MAX_MAX_CONTENT_TOKENS,
										);
									}}
									min={MIN_MAX_CONTENT_TOKENS}
									max={MAX_MAX_CONTENT_TOKENS}
									placeholder={t('Server default')}
								/>
								<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
									{t(
										'How much of each fetched page is read, in tokens, from 1000 to 100000. Leave blank to use the server default.',
									)}
								</p>
							</div>
						</div>
					)}
				</div>
			</div>

			{/* Which lines the lookup runs against */}
			<div>
				<h4
					className={`text-sm font-semibold mb-4 ${colors.classes.textPrimary}`}
				>
					{t('Lines to Look Up')}
				</h4>

				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('Lines to Enrich')}
					</label>
					<SingleSelect
						name="external-lookup-line-filter"
						value={lineFilter}
						onChange={(value) => {
							updateConfig(
								'lineFilter',
								value as ExternalLookupStepConfigProps['config']['lineFilter'],
							);
						}}
						options={LINE_FILTER_OPTIONS.map((option) => ({
							value: option.value,
							label: t(option.label),
						}))}
					/>
					<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t(
							'Which lines of the request get looked up. Lines that already matched well have nothing to gain.',
						)}
					</p>
				</div>

				{/* Only meaningful for the low-confidence filter */}
				{lineFilter === 'no_match_and_low_confidence' && (
					<div className="mb-4">
						<label
							className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
						>
							{t('Confidence Ceiling')}
						</label>
						<Input
							type="number"
							value={config.confidenceCeiling ?? ''}
							onChange={(e) => {
								updateClampedNumber(
									'confidenceCeiling',
									e.target.value,
									MIN_CONFIDENCE_CEILING,
									MAX_CONFIDENCE_CEILING,
								);
							}}
							min={MIN_CONFIDENCE_CEILING}
							max={MAX_CONFIDENCE_CEILING}
							placeholder={t('Server default')}
						/>
						<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
							{t(
								'A line is treated as low confidence when its best match scores at or below this value, from 0 to 100. Leave blank to use the matching profile default.',
							)}
						</p>
					</div>
				)}

				{/* Per-execution line cap */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('Maximum Lines')}
					</label>
					<Input
						type="number"
						value={config.maxLines ?? DEFAULT_MAX_LINES}
						onChange={(e) => {
							updateClampedNumber(
								'maxLines',
								e.target.value,
								MIN_MAX_LINES,
								MAX_MAX_LINES,
							);
						}}
						min={MIN_MAX_LINES}
						max={MAX_MAX_LINES}
					/>
					<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t(
							'How many eligible lines this step looks up. Extra lines are skipped with a warning. Default 50, maximum 200.',
						)}
					</p>
				</div>
			</div>

			{/* How the run behaves */}
			<div>
				<h4
					className={`text-sm font-semibold mb-4 ${colors.classes.textPrimary}`}
				>
					{t('Execution')}
				</h4>

				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('Concurrency')}
					</label>
					<Input
						type="number"
						value={config.concurrency ?? DEFAULT_CONCURRENCY}
						onChange={(e) => {
							updateClampedNumber(
								'concurrency',
								e.target.value,
								MIN_CONCURRENCY,
								MAX_CONCURRENCY,
							);
						}}
						min={MIN_CONCURRENCY}
						max={MAX_CONCURRENCY}
					/>
					<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t(
							'How many lines are looked up at once. Higher finishes sooner and puts more load on the sites being read. Default 4, maximum 8.',
						)}
					</p>
				</div>

				{/* Per-line wall clock — the fix for a lookup that reports a timeout */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('Lookup Timeout (seconds)')}
					</label>
					<Input
						type="number"
						value={config.timeoutSeconds ?? DEFAULT_TIMEOUT_SECONDS}
						onChange={(e) => {
							updateClampedNumber(
								'timeoutSeconds',
								e.target.value,
								MIN_TIMEOUT_SECONDS,
								MAX_TIMEOUT_SECONDS,
							);
						}}
						min={MIN_TIMEOUT_SECONDS}
						max={MAX_TIMEOUT_SECONDS}
					/>
					<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t(
							'How long each call of a line lookup may take before it is abandoned. Every search round and the final answer call get this allowance. Raise it if lines report that a search timed out. Default 180, maximum 600.',
						)}
					</p>
				</div>

				{/* Per-execution wall clock — bounds how long the step holds its slot */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('Execution Time Budget (seconds)')}
					</label>
					<Input
						type="number"
						value={config.wallClockSeconds ?? DEFAULT_WALL_CLOCK_SECONDS}
						onChange={(e) => {
							updateClampedNumber(
								'wallClockSeconds',
								e.target.value,
								MIN_WALL_CLOCK_SECONDS,
								MAX_WALL_CLOCK_SECONDS,
							);
						}}
						min={MIN_WALL_CLOCK_SECONDS}
						max={MAX_WALL_CLOCK_SECONDS}
					/>
					<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t(
							'How long the whole step may spend before it stops early. Lines already enriched are kept and re-matched. Default 300, maximum 1800.',
						)}
					</p>
				</div>

				{/* Per-execution token ceiling */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('Execution Token Budget')}
					</label>
					<Input
						type="number"
						value={config.maxTotalTokens ?? DEFAULT_MAX_TOTAL_TOKENS}
						onChange={(e) => {
							updateClampedNumber(
								'maxTotalTokens',
								e.target.value,
								MIN_MAX_TOTAL_TOKENS,
								MAX_MAX_TOTAL_TOKENS,
							);
						}}
						min={MIN_MAX_TOTAL_TOKENS}
						max={MAX_MAX_TOTAL_TOKENS}
					/>
					<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t(
							'How many tokens one run of this step may spend on lookups. Reaching it stops the run early and keeps what it enriched. Default 500000, maximum 5000000.',
						)}
					</p>
				</div>

				{/* Cumulative ceiling across runs — the guard against a repeated flow */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('Request Token Budget')}
					</label>
					<Input
						type="number"
						value={config.requestTokenBudget ?? DEFAULT_REQUEST_TOKEN_BUDGET}
						onChange={(e) => {
							updateClampedNumber(
								'requestTokenBudget',
								e.target.value,
								MIN_REQUEST_TOKEN_BUDGET,
								MAX_REQUEST_TOKEN_BUDGET,
							);
						}}
						min={MIN_REQUEST_TOKEN_BUDGET}
						max={MAX_REQUEST_TOKEN_BUDGET}
					/>
					<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t(
							'Total tokens allowed for one request across every run of this step, not just this one. A request that has already used them is skipped with a warning, so a repeated flow cannot keep paying. Default 2000000, maximum 20000000.',
						)}
					</p>
				</div>

				{/* Re-score after enrichment */}
				<div>
					<label className="flex items-center gap-2 cursor-pointer">
						<input
							type="checkbox"
							checked={config.rematch !== false}
							onChange={(e) => {
								updateConfig('rematch', e.target.checked);
							}}
						/>
						<span
							className={`text-sm font-medium ${colors.classes.textPrimary}`}
						>
							{t('Re-match after enrichment')}
						</span>
					</label>
					<p className={`ml-6 text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t(
							'Scores the request again once the new facts are on the lines, so the review queue reflects them. Turn this off only to inspect the enrichment on its own.',
						)}
					</p>
				</div>
			</div>

			{/* Optional refinements — collapsed by default */}
			<div
				className={`border rounded-lg ${colors.classes.borderDefault} overflow-hidden`}
			>
				<button
					type="button"
					onClick={() => {
						setAdvancedExpanded(!advancedExpanded);
					}}
					className={`w-full flex items-center gap-2 px-3 py-2.5 text-left ${colors.classes.bgSecondary}`}
				>
					<Icon
						icon={advancedExpanded ? faChevronDown : faChevronRight}
						className={`text-xs ${colors.classes.textSecondary}`}
					/>
					<span className={`text-sm font-medium ${colors.classes.textPrimary}`}>
						{t('Advanced')}
					</span>
				</button>

				{advancedExpanded && (
					<div className="p-3 space-y-4">
						{/* Free-text operator guidance */}
						<div>
							<label
								className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
							>
								{t('Lookup Instructions')}
							</label>
							<TextArea
								value={lookupConstraintsValue}
								onChange={(e) => {
									updateConfig(
										'lookupConstraints',
										e.target.value === '' ? undefined : e.target.value,
									);
								}}
								rows={4}
								maxLength={MAX_LOOKUP_CONSTRAINTS_LENGTH}
								placeholder={t(
									'For example: prefer manufacturer datasheets; ignore marketplace listings',
								)}
							/>
							<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
								{t(
									'Optional guidance folded into the lookup. It steers what the lookup looks for and which pages it prefers. It does not change which domains are allowed.',
								)}
							</p>
							<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
								{t('Characters used')}
								{t(':')} {t(String(lookupConstraintsValue.length))} {t('of')}{' '}
								{t(String(MAX_LOOKUP_CONSTRAINTS_LENGTH))}
							</p>
						</div>

						{/* Model override */}
						<div>
							<label
								className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
							>
								{t('Model')}
							</label>
							<Input
								type="text"
								value={config.model ?? ''}
								onChange={(e) => {
									updateConfig(
										'model',
										e.target.value === '' ? undefined : e.target.value,
									);
								}}
								placeholder={t('Server default')}
							/>
							<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
								{t(
									'The model that runs the lookup. Leave blank unless you have been told to set a specific one.',
								)}
							</p>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
